/* ============================================================================
   hewyn-ui.js
   The behaviour half of the Hewyn interaction and material kit.

   Pairs with hewyn-ui.css. Everything is driven by data attributes, so an
   existing section adopts a widget by adding classes and one attribute, not by
   importing anything or restructuring its markup.

   Public surface:
     HewynUI.init(root)                 wire everything inside root. Idempotent.
     HewynUI.ring(el, value)            animate a ring to a new value
     HewynUI.number(el, value)          roll a number readout to a new value
     HewynUI.band(el, ratio)            travel a zone marker to 0..1
     HewynUI.openSheet(id) / closeSheet(id)
     HewynUI.reduced()                  true when the user asked for less motion
     HewynUI.swap(el, to)               skeleton -> content, or back

   The reduced-motion contract, in code:
     reduced() is read at call time, not cached, so a user changing the setting
     mid-session is respected. When it is true, every routine below sets its
     final value immediately and returns. Nothing is skipped, nothing throws,
     no state is left half applied.

   The layout-property rule, in code:
     The only routine here that touches a layout property is the disclosure,
     which interpolates height through the Web Animations API on a panel
     carrying contain: content. That containment is what keeps the reflow
     inside the panel instead of letting it walk the whole page. Every other
     routine animates transform, opacity or a paint property.
   ========================================================================= */

(function (global) {
  "use strict";

  var RM = "(prefers-reduced-motion: reduce)";

  function reduced() {
    return !!(global.matchMedia && global.matchMedia(RM).matches);
  }

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  /* The "already wired" flag. It deliberately does NOT reuse the mount
     attribute's own name: data-hui-tile carries the tile's group name, and
     stamping "1" onto it would erase the group and break single select. */
  function once(el, key) {
    if (el.getAttribute("data-hui-is-" + key) === "1") return false;
    el.setAttribute("data-hui-is-" + key, "1");
    return true;
  }
  function clamp(n, a, b) { return n < a ? a : n > b ? b : n; }

  /* =========================================================================
     PRESS
     A CSS :active alone is not enough on iOS, where a scroll that starts on a
     control leaves the active state stuck, and a tap that lands during
     momentum never gets one at all. This tracks the pointer and writes
     data-pressed, which the stylesheet styles identically to :active, then
     clears it on any cancel, scroll or pointer leave.
     ====================================================================== */
  function wirePress(el) {
    if (!once(el, "press")) return;
    var down = false;
    function set(on) {
      down = on;
      if (on) el.setAttribute("data-pressed", "1");
      else el.removeAttribute("data-pressed");
    }
    el.addEventListener("pointerdown", function () {
      if (el.disabled || el.getAttribute("aria-disabled") === "true") return;
      set(true);
    });
    ["pointerup", "pointercancel", "pointerleave", "blur"].forEach(function (evt) {
      el.addEventListener(evt, function () { if (down) set(false); });
    });
    global.addEventListener("scroll", function () { if (down) set(false); }, { passive: true });
  }

  /* =========================================================================
     PROGRESS RING
     Builds its own SVG so a section only has to write:
       <div class="hui-ring" data-hui-ring data-value="78" data-size="120"></div>
     The arc animates through stroke-dashoffset, a paint property, and the
     numeral counts through the rolling-number widget rather than swapping.
     ====================================================================== */
  function buildRing(el) {
    if (!once(el, "ring")) return;
    var size = parseFloat(el.getAttribute("data-size") || 120);
    var stroke = parseFloat(el.getAttribute("data-stroke") || 10);
    var r = (size - stroke) / 2;
    var c = 2 * Math.PI * r;
    var max = parseFloat(el.getAttribute("data-max") || 100);
    var unit = el.getAttribute("data-unit") || "";

    var ns = "http://www.w3.org/2000/svg";
    var svg = document.createElementNS(ns, "svg");
    svg.setAttribute("width", size); svg.setAttribute("height", size);
    svg.setAttribute("viewBox", "0 0 " + size + " " + size);
    svg.setAttribute("aria-hidden", "true");

    function circle(cls) {
      var el2 = document.createElementNS(ns, "circle");
      el2.setAttribute("cx", size / 2); el2.setAttribute("cy", size / 2);
      el2.setAttribute("r", r); el2.setAttribute("stroke-width", stroke);
      el2.setAttribute("class", cls);
      return el2;
    }
    var track = circle("hui-ring__track");
    var fill = circle("hui-ring__fill");
    fill.setAttribute("stroke-dasharray", c);
    fill.setAttribute("stroke-dashoffset", c);   /* starts empty, then draws */
    svg.appendChild(track); svg.appendChild(fill);
    el.appendChild(svg);

    var centre = document.createElement("div");
    centre.className = "hui-ring__centre";
    var inner = document.createElement("div");
    var val = document.createElement("span");
    val.className = "hui-ring__value hui-num";
    val.style.fontSize = Math.round(size * 0.29) + "px";
    val.setAttribute("data-hui-number", "");
    val.setAttribute("data-value", "0");
    if (unit) val.setAttribute("data-unit", unit);
    inner.appendChild(val);

    var cap = el.getAttribute("data-caption");
    if (cap) {
      var capEl = document.createElement("div");
      capEl.className = "hui-label";
      capEl.style.marginTop = "5px";
      capEl.textContent = cap;
      inner.appendChild(capEl);
    }
    centre.appendChild(inner);
    el.appendChild(centre);

    el.__hui = { fill: fill, c: c, max: max, val: val, value: 0 };
    buildNumber(val);

    var target = parseFloat(el.getAttribute("data-value") || 0);
    /* Draw on entry, not on load, so a ring below the fold still performs when
       it is actually seen. */
    whenSeen(el, function () { ring(el, target); });
  }

  function ring(el, value) {
    var s = el.__hui;
    if (!s) return;
    value = clamp(value, 0, s.max);
    s.value = value;
    el.setAttribute("data-value", value);
    var offset = s.c * (1 - value / s.max);
    if (reduced()) {
      s.fill.style.transition = "none";
      s.fill.setAttribute("stroke-dashoffset", offset);
      /* force the style to land before the transition is restored */
      void s.fill.getBoundingClientRect();
      s.fill.style.transition = "";
    } else {
      s.fill.setAttribute("stroke-dashoffset", offset);
    }
    number(s.val, value);
  }

  /* =========================================================================
     ROLLING NUMBER
     Each digit is a 0..9 column translated on Y. Fixed-width digit cells plus
     tabular figures mean the unit beside the number never moves, even when the
     value goes from 9 to 10.
     ====================================================================== */
  function buildNumber(el) {
    if (!once(el, "number")) return;
    el.classList.add("hui-num");
    el.__hui = { text: "" };
    var unit = el.getAttribute("data-unit");
    if (unit) el.__hui.unit = unit;
    var start = el.getAttribute("data-value");
    number(el, start === null ? 0 : parseFloat(start), true);
  }

  function number(el, value, immediate) {
    if (!el.__hui) buildNumber(el);
    var decimals = parseInt(el.getAttribute("data-decimals") || "0", 10);
    var text = Number(value).toFixed(decimals);
    var prefix = el.getAttribute("data-prefix") || "";
    var full = prefix + text;
    el.setAttribute("data-value", value);

    var state = el.__hui;
    if (state.text.length !== full.length) {
      /* The character count changed, so rebuild the cells. Rebuilding is the
         only way a digit can appear or disappear without the row shifting,
         because every cell is the same fixed width. */
      el.innerHTML = "";
      for (var i = 0; i < full.length; i++) {
        var ch = full.charAt(i);
        if (ch >= "0" && ch <= "9") {
          var cell = document.createElement("span");
          cell.className = "hui-num__digit";
          var col = document.createElement("span");
          col.className = "hui-num__col";
          for (var d = 0; d <= 9; d++) {
            var s = document.createElement("span");
            s.textContent = String(d);
            col.appendChild(s);
          }
          cell.appendChild(col);
          el.appendChild(cell);
        } else {
          var stat = document.createElement("span");
          stat.className = "hui-num__static";
          stat.textContent = ch;
          el.appendChild(stat);
        }
      }
      if (state.unit) {
        var u = document.createElement("span");
        u.className = "hui-num__unit";
        u.textContent = state.unit;
        el.appendChild(u);
      }
      state.text = "";
    }

    var cols = $$(".hui-num__col", el);
    var ci = 0;
    for (var j = 0; j < full.length; j++) {
      var c2 = full.charAt(j);
      if (c2 >= "0" && c2 <= "9") {
        var colEl = cols[ci++];
        if (!colEl) continue;
        if (immediate || reduced()) {
          colEl.style.transition = "none";
          colEl.style.transform = "translate3d(0," + (-parseInt(c2, 10)) + "em,0)";
          void colEl.offsetHeight;
          colEl.style.transition = "";
        } else {
          /* Stagger by digit so the number reads as rolling rather than as a
             row of counters all landing on the same frame. Least significant
             digit first, which is how a real odometer settles. */
          colEl.style.transitionDelay = ((full.length - j - 1) * 28) + "ms";
          colEl.style.transform = "translate3d(0," + (-parseInt(c2, 10)) + "em,0)";
        }
      }
    }
    state.text = full;
    el.setAttribute("aria-label", full + (state.unit ? " " + state.unit : ""));
  }

  /* =========================================================================
     ZONE BAND
     The marker travels from the left edge to its position on entry.
     ====================================================================== */
  function buildBand(el) {
    if (!once(el, "band")) return;
    var marker = $(".hui-band__marker", el);
    if (!marker) return;
    el.__hui = { marker: marker };
    var ratio = parseFloat(el.getAttribute("data-value") || 0);
    whenSeen(el, function () { band(el, ratio); });
  }

  function band(el, ratio) {
    var s = el.__hui;
    if (!s) return;
    ratio = clamp(ratio, 0, 1);
    el.setAttribute("data-value", ratio);
    var track = $(".hui-band__track", el);
    var w = track ? track.getBoundingClientRect().width : 0;
    var x = Math.round(w * ratio);
    if (reduced()) {
      s.marker.style.transition = "none";
      s.marker.style.transform = "translate3d(" + x + "px,0,0)";
      void s.marker.offsetHeight;
      s.marker.style.transition = "";
    } else {
      s.marker.style.transform = "translate3d(" + x + "px,0,0)";
    }
  }

  /* =========================================================================
     DISCLOSURE
     Interpolated height, contained. The panel is measured, animated from its
     current height to its natural height, and then released to auto so a later
     content change does not leave it pinned at a stale pixel value.
     ====================================================================== */
  function buildDisclosure(el) {
    if (!once(el, "disc")) return;
    var head = $(".hui-disc__head", el);
    var panel = $(".hui-disc__panel", el);
    if (!head || !panel) return;
    var open = el.getAttribute("data-open") === "1";
    panel.style.height = open ? "auto" : "0px";
    panel.style.overflow = "hidden";
    head.setAttribute("aria-expanded", open ? "true" : "false");
    head.addEventListener("click", function () { toggleDisclosure(el); });
  }

  function toggleDisclosure(el, force) {
    var head = $(".hui-disc__head", el);
    var panel = $(".hui-disc__panel", el);
    var open = el.getAttribute("data-open") === "1";
    var next = typeof force === "boolean" ? force : !open;
    if (next === open) return;
    el.setAttribute("data-open", next ? "1" : "0");
    head.setAttribute("aria-expanded", next ? "true" : "false");

    var from = panel.getBoundingClientRect().height;
    panel.style.height = "auto";
    var to = next ? panel.getBoundingClientRect().height : 0;
    panel.style.height = from + "px";

    if (reduced() || !panel.animate) {
      panel.style.height = next ? "auto" : "0px";
      return;
    }
    void panel.offsetHeight;
    var anim = panel.animate(
      [{ height: from + "px" }, { height: to + "px" }],
      { duration: 320, easing: "cubic-bezier(.22,1,.36,1)", fill: "both" }
    );
    anim.onfinish = function () {
      anim.cancel();
      panel.style.height = next ? "auto" : "0px";
    };
  }

  /* =========================================================================
     SEGMENTED CONTROL
     One indicator, moved with translateX and scaled on X to the active
     segment's width. Keyboard arrows move the selection, which is what a
     native segmented control does and what a screen reader user expects.
     ====================================================================== */
  function buildSeg(el) {
    if (!once(el, "seg")) return;
    var ind = $(".hui-seg__ind", el);
    var btns = $$(".hui-seg__btn", el);
    if (!ind || !btns.length) return;
    el.setAttribute("role", "tablist");

    function place(btn, animate) {
      var pad = 4;
      var x = btn.offsetLeft;
      var w = btn.offsetWidth;
      if (!animate || reduced()) {
        ind.style.transition = "none";
        ind.style.transform = "translate3d(" + x + "px,0,0) scaleX(" + w + ")";
        void ind.offsetHeight;
        ind.style.transition = "";
      } else {
        ind.style.transform = "translate3d(" + x + "px,0,0) scaleX(" + w + ")";
      }
      void pad;
    }

    function select(btn, animate) {
      btns.forEach(function (b) {
        var on = b === btn;
        b.setAttribute("aria-selected", on ? "true" : "false");
        b.setAttribute("tabindex", on ? "0" : "-1");
      });
      place(btn, animate);
      el.dispatchEvent(new CustomEvent("hui:segment", { bubbles: true, detail: { value: btn.getAttribute("data-value"), button: btn } }));
    }

    btns.forEach(function (b, i) {
      b.setAttribute("role", "tab");
      b.addEventListener("click", function () { select(b, true); });
      b.addEventListener("keydown", function (e) {
        var d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = btns[(i + d + btns.length) % btns.length];
        n.focus(); select(n, true);
      });
    });

    var initial = btns.filter(function (b) { return b.getAttribute("aria-selected") === "true"; })[0] || btns[0];
    /* Place without animating on first paint, and re-place on resize, because
       the indicator's scale is in pixels and a reflowed control would leave it
       measuring the old width. */
    requestAnimationFrame(function () { select(initial, false); });
    var t;
    global.addEventListener("resize", function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var cur = btns.filter(function (b) { return b.getAttribute("aria-selected") === "true"; })[0] || btns[0];
        place(cur, false);
      }, 120);
    });
  }

  /* =========================================================================
     BOTTOM SHEET
     Springs up, drags down, and never moves the page behind it. Body scroll is
     locked with position rather than overflow so iOS does not scroll the page
     under the sheet, and the scroll position is restored exactly on close.
     ====================================================================== */
  var scrollLock = { y: 0, count: 0 };
  function lockBody(on) {
    var b = document.body;
    if (on) {
      if (scrollLock.count++ > 0) return;
      scrollLock.y = global.scrollY || 0;
      b.style.position = "fixed";
      b.style.top = (-scrollLock.y) + "px";
      b.style.left = "0"; b.style.right = "0";
      b.style.width = "100%";
    } else {
      if (--scrollLock.count > 0) return;
      scrollLock.count = 0;
      b.style.position = ""; b.style.top = ""; b.style.left = ""; b.style.right = ""; b.style.width = "";
      global.scrollTo(0, scrollLock.y);
    }
  }

  function buildSheet(el) {
    if (!once(el, "sheet")) return;
    var id = el.id;
    var backdrop = document.querySelector('[data-hui-sheet-backdrop="' + id + '"]');
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.setAttribute("data-open", "0");
    if (backdrop) backdrop.addEventListener("click", function () { closeSheet(id); });

    $$("[data-hui-sheet-close]", el).forEach(function (b) {
      b.addEventListener("click", function () { closeSheet(id); });
    });

    /* Drag to dismiss. The transition is removed while the finger is down so
       the sheet tracks exactly, then restored on release so it springs either
       home or away. Past 96px or a fast flick, it closes. */
    var grip = $(".hui-sheet__grip", el);
    if (grip) {
      var startY = 0, dy = 0, t0 = 0, dragging = false;
      grip.addEventListener("pointerdown", function (e) {
        dragging = true; startY = e.clientY; dy = 0; t0 = Date.now();
        el.setAttribute("data-dragging", "1");
        grip.setPointerCapture(e.pointerId);
      });
      grip.addEventListener("pointermove", function (e) {
        if (!dragging) return;
        dy = Math.max(0, e.clientY - startY);
        el.style.transform = "translate3d(0," + dy + "px,0)";
      });
      function end() {
        if (!dragging) return;
        dragging = false;
        el.removeAttribute("data-dragging");
        el.style.transform = "";
        var v = dy / Math.max(1, Date.now() - t0);
        if (dy > 96 || v > 0.6) closeSheet(id);
      }
      grip.addEventListener("pointerup", end);
      grip.addEventListener("pointercancel", end);
    }

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && el.getAttribute("data-open") === "1") closeSheet(id);
    });
  }

  function openSheet(id) {
    var el = document.getElementById(id);
    if (!el || el.getAttribute("data-open") === "1") return;
    var backdrop = document.querySelector('[data-hui-sheet-backdrop="' + id + '"]');
    lockBody(true);
    el.setAttribute("data-open", "1");
    if (backdrop) backdrop.setAttribute("data-open", "1");
    var focusable = el.querySelector("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])");
    if (focusable) setTimeout(function () { focusable.focus(); }, reduced() ? 0 : 340);
  }

  function closeSheet(id) {
    var el = document.getElementById(id);
    if (!el || el.getAttribute("data-open") !== "1") return;
    var backdrop = document.querySelector('[data-hui-sheet-backdrop="' + id + '"]');
    el.setAttribute("data-open", "0");
    if (backdrop) backdrop.removeAttribute("data-open");
    lockBody(false);
    var opener = document.querySelector('[data-hui-sheet-open="' + id + '"]');
    if (opener) opener.focus();
  }

  /* =========================================================================
     TOGGLE, TILE, CHIP
     Small, but they carry most of the taps, so they get real keyboard support
     and emit an event rather than assuming what the host section wants to do.
     ====================================================================== */
  function buildToggle(el) {
    if (!once(el, "toggle")) return;
    el.setAttribute("role", "switch");
    if (!el.hasAttribute("aria-checked")) el.setAttribute("aria-checked", "false");
    if (!el.querySelector(".hui-toggle__knob")) {
      var k = document.createElement("span");
      k.className = "hui-toggle__knob";
      el.appendChild(k);
    }
    el.addEventListener("click", function () {
      if (el.disabled) return;
      var next = el.getAttribute("aria-checked") !== "true";
      el.setAttribute("aria-checked", next ? "true" : "false");
      el.dispatchEvent(new CustomEvent("hui:toggle", { bubbles: true, detail: { checked: next } }));
    });
  }

  function buildTile(el) {
    if (!once(el, "tile")) return;
    wirePress(el);
    var group = el.getAttribute("data-hui-tile");   /* "" = multi, name = single */
    if (!el.querySelector(".hui-tile__check")) {
      var c = document.createElement("span");
      c.className = "hui-tile__check";
      c.setAttribute("aria-hidden", "true");
      /* Drawn, not typed. A check glyph is an emoji in some fonts and a
         different weight in every other one, and the house rule is no emoji. */
      c.innerHTML = '<svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">' +
        '<path d="M1 4.6 4 7.6 10 1.4" stroke="currentColor" stroke-width="1.8" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      el.appendChild(c);
    }
    if (!el.hasAttribute("aria-pressed")) el.setAttribute("aria-pressed", "false");
    el.addEventListener("click", function () {
      if (el.disabled) return;
      if (group) {
        $$('[data-hui-tile="' + group + '"]').forEach(function (o) { o.setAttribute("aria-pressed", o === el ? "true" : "false"); });
      } else {
        el.setAttribute("aria-pressed", el.getAttribute("aria-pressed") === "true" ? "false" : "true");
      }
      el.dispatchEvent(new CustomEvent("hui:tile", { bubbles: true, detail: { value: el.getAttribute("data-value"), selected: el.getAttribute("aria-pressed") === "true" } }));
    });
  }

  function buildChip(el) {
    if (!once(el, "chip")) return;
    if (!el.hasAttribute("aria-pressed")) el.setAttribute("aria-pressed", "false");
    el.addEventListener("click", function () {
      if (el.disabled) return;
      el.setAttribute("aria-pressed", el.getAttribute("aria-pressed") === "true" ? "false" : "true");
      el.dispatchEvent(new CustomEvent("hui:chip", { bubbles: true, detail: { value: el.getAttribute("data-value"), on: el.getAttribute("aria-pressed") === "true" } }));
    });
  }

  /* =========================================================================
     SKELETON SWAP
     ====================================================================== */
  function swap(el, to) {
    var sk = $('[data-swap="skeleton"]', el);
    var ct = $('[data-swap="content"]', el);
    if (!sk || !ct) return;
    if (to === "content") { sk.setAttribute("data-hidden", "1"); ct.removeAttribute("data-hidden"); }
    else { ct.setAttribute("data-hidden", "1"); sk.removeAttribute("data-hidden"); }
  }

  /* =========================================================================
     ENTRY OBSERVER
     Anything that draws or travels on entry uses this, so a ring below the
     fold performs when it is seen rather than silently finishing while the
     customer is still reading the header.
     ====================================================================== */
  function whenSeen(el, fn) {
    if (!global.IntersectionObserver) { requestAnimationFrame(fn); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.disconnect();
        requestAnimationFrame(function () { requestAnimationFrame(fn); });
      });
    }, { threshold: 0.25 });
    io.observe(el);
  }

  /* Reveal on entry: opacity plus an 10px rise, transform only. */
  function buildReveal(el) {
    if (!once(el, "reveal")) return;
    if (reduced()) { el.style.opacity = "1"; el.style.transform = "none"; return; }
    el.style.opacity = "0";
    el.style.transform = "translate3d(0,10px,0)";
    el.style.transition = "opacity 320ms cubic-bezier(.22,1,.36,1), transform 320ms cubic-bezier(.22,1,.36,1)";
    var delay = parseInt(el.getAttribute("data-hui-reveal") || "0", 10) || 0;
    whenSeen(el, function () {
      el.style.transitionDelay = delay + "ms";
      el.style.opacity = "1";
      el.style.transform = "translate3d(0,0,0)";
    });
  }

  /* =========================================================================
     INIT
     ====================================================================== */
  function init(root) {
    root = root || document;
    $$(".hui-press", root).forEach(wirePress);
    $$("[data-hui-toggle]", root).forEach(buildToggle);
    $$("[data-hui-tile]", root).forEach(buildTile);
    $$("[data-hui-chip]", root).forEach(buildChip);
    $$("[data-hui-ring]", root).forEach(buildRing);
    $$("[data-hui-number]", root).forEach(buildNumber);
    $$("[data-hui-band]", root).forEach(buildBand);
    $$("[data-hui-disc]", root).forEach(buildDisclosure);
    $$("[data-hui-seg]", root).forEach(buildSeg);
    $$("[data-hui-sheet]", root).forEach(buildSheet);
    $$("[data-hui-reveal]", root).forEach(buildReveal);
    $$("[data-hui-sheet-open]", root).forEach(function (b) {
      if (!once(b, "sheetopen")) return;
      b.addEventListener("click", function () { openSheet(b.getAttribute("data-hui-sheet-open")); });
    });
  }

  var HewynUI = {
    init: init,
    reduced: reduced,
    ring: ring,
    number: number,
    band: band,
    swap: swap,
    openSheet: openSheet,
    closeSheet: closeSheet,
    toggleDisclosure: toggleDisclosure,
    version: "1.0.0"
  };

  global.HewynUI = HewynUI;
  if (typeof module !== "undefined" && module.exports) module.exports = HewynUI;

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { init(document); });
  else init(document);

})(typeof window !== "undefined" ? window : this);
